import React from 'react';
import { useTranslation } from 'react-i18next';
import CustomTable from '../../../../../../../../components/table/CustomTable';
import CustomTh from '../../../../../../../../components/table/CustomTh';
import CustomTd from '../../../../../../../../components/table/CustomTd';
import DragHandleCell from '../../../../../../../../components/table/DragHandleCell';
import CustomChip from '../../../../../../../../components/CustomChip';
import { colors } from '../../../../../../../../enums/colors';
import { getCalculationTypeById, POLICY_CONDITION_MODEL } from '../Model/Model';
import { EMPTY_VALUES } from '../../../../../../../../Apis/functions';
import ScopeCell from './ScopeCell';
import { CustomSwitch } from '../../../../../../../../components/CustomSwitch';
import { STATUS_MODEL } from '../../../../../../../../enums/statusEnum';
import {TrashOutlineIcon} from '../../../../../../../../components/buttons/IconButtons';
import ButtonIcon from "../../../../../../../../components/buttons/ButtonIcon";
import {DRAG_HANDLE_WIDTH} from "../../../../../../../../components/table/Model/TableModel";

const tableId = 'tbl_rst_rules_summary';

/**
 * Renders a summary table of overtime policy rules.
 * @param {Array} rules - List of rule objects to display.
 * @param {boolean} isGlobal - When true hides the name column (global rules share the policy name).
 * @param {boolean} sortable - Enables drag-and-drop row reordering.
 * @param {Function} onDragEnd - Callback fired when a drag-and-drop sort completes.
 * @param {boolean} showStatus - When true renders the status toggle column.
 * @param {Function|null} onStatusChange - Called with the rule when its status toggle is clicked. Column is read-only when null.
 * @param onDeleteRule
 */
const RulesTable = ({
                        rules = [],
                        isGlobal,
                        sortable = false,
                        onDragEnd,
                        showStatus = false,
                        onStatusChange = null,
                        onDeleteRule = null
}) => {
    const { t } = useTranslation();

    const thead = [
        sortable && <CustomTh tableId={tableId} customWidth={DRAG_HANDLE_WIDTH}/>,
        <CustomTh tableId={tableId} classes="first_head">#</CustomTh>,
        !isGlobal && <CustomTh tableId={tableId}>{t('name')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('condition')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('calculation_type')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('calculation_value')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('scope')}</CustomTh>,
        showStatus && <CustomTh tableId={tableId}>{t('status')}</CustomTh>,
        onDeleteRule && <CustomTh tableId={tableId} customWidth={40}>{t('remove')}</CustomTh>
    ].filter(Boolean);

    // Preserve real id when available; fall back to index for unsaved rules (id: null)
    const data = rules.map((rule, i) => ({ ...rule, id: rule.id ?? i }));

    const body = data.map((rule, index) => {
        const trigger = POLICY_CONDITION_MODEL.values().find(v => v.key === rule.condition);
        const conditionTrans = trigger ? t(trigger.trans) : (rule.condition || EMPTY_VALUES.DEFAULT);
        const type = getCalculationTypeById(rule.calculationType);

        return <>
            {sortable && <DragHandleCell tableId={tableId} />}
            <CustomTd tableId={tableId} classes="first-column">{index + 1}</CustomTd>
            {!isGlobal && <CustomTd tableId={tableId}>{rule.name}</CustomTd>}
            <CustomTd tableId={tableId}>
                <CustomChip
                    background={colors.secondary100}
                    fontColor={colors.secondary700}
                    borderColor={colors.secondary150}
                    legend={t(conditionTrans)}
                    variant='compact'
                />
            </CustomTd>
            <CustomTd tableId={tableId}>
                <CustomChip
                    background={colors.shades100}
                    fontColor={colors.shades700}
                    borderColor={colors.shades150}
                    legend={type?.name}
                    variant='large'
                />
            </CustomTd>
            <CustomTd tableId={tableId}>
                <CustomChip
                    background={colors.shades100}
                    fontColor={colors.shades700}
                    borderColor={colors.shades150}
                    legend={`${type?.prefixText} ${rule.calculationValue}`}
                    variant='large'
                />
            </CustomTd>
            <CustomTd tableId={tableId}>
                <ScopeCell rule={rule} t={t} />
            </CustomTd>
            {showStatus && (
                <CustomTd tableId={tableId}>
                    <CustomSwitch
                        id={`cb_rst_status_${index}`}
                        name="status"
                        checked={rule?.statusId === STATUS_MODEL.ACTIVE_COMPANY}
                        showBorder={false}
                        className=""
                        disabled={!onStatusChange}
                        onChange={() => onStatusChange?.(rule)}
                    />
                </CustomTd>
            )}
            {onDeleteRule && (
                <CustomTd tableId={tableId}>
                    <ButtonIcon
                        id={`btn_rt_delete_rule_${index}`}
                        onClick={() => onDeleteRule(index)}
                        Icon={TrashOutlineIcon}
                        className='btn-transparent'
                        iconProps={"icon-color-shades-400"}
                    />
                </CustomTd>
            )}
        </>;
    });

    return (
        <CustomTable
            tableId={tableId}
            thead={thead}
            tbody={{ body, data }}
            columnActions={false}
            checkbox={false}
            sortable={sortable}
            onDragEnd={onDragEnd}
            withoutHeight={true}
        />
    );
};

export default RulesTable;
